import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
    Box, Paper, Typography, Button, TextField, IconButton,
    Radio, RadioGroup, FormControlLabel, FormControl, Chip,
    List, ListItem, ListItemText, Divider, Alert, Dialog,
    DialogTitle, DialogContent, DialogActions, Card, CardContent,
    Select, MenuItem, InputLabel, Collapse
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import QuizIcon from '@mui/icons-material/Quiz';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SendIcon from '@mui/icons-material/Send';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
    getMyMonouQuestions, postMonouQuestion, updateMonouQuestion,
    deleteMonouQuestion, getPublishedMonouQuizzes, submitMonouAnswer,
    getMyMonouAnswers, getMonouRanking, getAllMonouQuestions,
    getAllMonouQuizzes, selectMonouQuestion, createMonouQuiz,
    createMonouQuizChoice, publishMonouQuiz, updateMonouQuiz, deleteMonouQuiz,
    bulkPublishMonouQuizzes, toggleMonouResultPublish, getMonouResultPublishStatus
} from '../services/api';

interface MonouQuestion {
    id: number;
    text: string;
    created_by: number;
    created_by_name: string;
    created_at: string;
    updated_at: string;
    is_selected: boolean;
    is_published: boolean;
}

interface MonouQuizChoice {
    id: number;
    choice_text: string;
    choice_order: number;
    is_correct: boolean;
}

interface MonouQuiz {
    id: number;
    question: number;
    question_text: string;
    correct_answer: string;
    created_by: number;
    created_by_name: string;
    created_at: string;
    order: number;
    is_published: boolean;
    choices: MonouQuizChoice[];
    question_created_by_name?: string;
}

interface MonouUserAnswer {
    id: number;
    user: number;
    user_name: string;
    quiz: number;
    quiz_text: string;
    selected_choice: number;
    is_correct: boolean;
    answered_at: string;
}

interface RankingEntry {
    user__id: number;
    user__name: string;
    correct_count: number;
    selected_count: number;
}

const HEADER_HEIGHT = 64;

const MonouRoom: React.FC = () => {
    const { token, isStaff, refreshTokenFunction, logout, isLoading } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    

    // 質問投稿フォーム
    const [questionText, setQuestionText] = useState('');
    const [myQuestions, setMyQuestions] = useState<MonouQuestion[]>([]);
    const [editingQuestionId, setEditingQuestionId] = useState<number | null>(null);
    const [editingQuestionText, setEditingQuestionText] = useState('');

    // クイズ
    const [publishedQuizzes, setPublishedQuizzes] = useState<MonouQuiz[]>([]);
    const [currentQuizIndex, setCurrentQuizIndex] = useState(0);
    const [selectedChoice, setSelectedChoice] = useState<number | null>(null);
    const [myAnswers, setMyAnswers] = useState<MonouUserAnswer[]>([]);

    // ランキング
    const [ranking, setRanking] = useState<RankingEntry[]>([]);
    const [showRanking, setShowRanking] = useState(false);
    const [selectedQuizForDetail, setSelectedQuizForDetail] = useState<number | ''>('');
    const [quizAnswerDetails, setQuizAnswerDetails] = useState<MonouUserAnswer[]>([]);

    // スタッフ用
    const [allQuestions, setAllQuestions] = useState<MonouQuestion[]>([]);
    const [allQuizzes, setAllQuizzes] = useState<MonouQuiz[]>([]);
    const [showStaffPanel, setShowStaffPanel] = useState(false);
    const [expandQuestions, setExpandQuestions] = useState(false); // 投稿された質問一覧の展開状態

    // スタッフ用: クイズ作成フォーム
    const [newQuizQuestionId, setNewQuizQuestionId] = useState<number | ''>('');
    // 問題文入力は不要。質問テキストをそのまま使用する。
    const [choiceTexts, setChoiceTexts] = useState<string[]>(['', '', '', '']);
    const [correctIndex, setCorrectIndex] = useState<number | null>(null);

    // エラー・成功メッセージ
    const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    // クイズ回答数
    const [quizAnswerCounts, setQuizAnswerCounts] = useState<{ [quizId: number]: number }>({});

    // 質問投稿期限
    const [questionDeadline, setQuestionDeadline] = useState<Date | null>(null);
    const [isQuestionPeriodActive, setIsQuestionPeriodActive] = useState(true);

    // 結果発表状態
    const [isResultPublished, setIsResultPublished] = useState(false);

    // メッセージ自動消去タイマー
    useEffect(() => {
        if (message) {
            const timer = setTimeout(() => setMessage(null), 3000); // 3秒後に自動消去
            return () => clearTimeout(timer);
        }
    }, [message]);

    // 3秒ごとに回答数を更新
    useEffect(() => {
        if (!token || !showStaffPanel) return;
        
        const updateAnswerCounts = async () => {
            try {
                const res = await getMyMonouAnswers(token);
                const counts: { [quizId: number]: number } = {};
                res.data.forEach((answer: any) => {
                    counts[answer.quiz] = (counts[answer.quiz] || 0) + 1;
                });
                setQuizAnswerCounts(counts);
            } catch (error) {
                // エラーは無視
            }
        };

        updateAnswerCounts();
        const interval = setInterval(updateAnswerCounts, 3000);
        return () => clearInterval(interval);
    }, [token, showStaffPanel]);

    useEffect(() => {
        // 認証状態のローディング中は待機
        if (isLoading) return;
        
        if (!token) {
            navigate('/login', { replace: true });
        } else {
            fetchData();
        }

        // 質問投稿期限を読み込み
        fetch('/config/event-timer2.json')
            .then(res => res.json())
            .then(data => {
                const deadline = new Date(data.endTime);
                setQuestionDeadline(deadline);
                setIsQuestionPeriodActive(new Date() < deadline);
            })
            .catch(err => {
                console.error('Failed to load deadline:', err);
            });
    }, [token, navigate, isLoading, isStaff]);

    // 質問期限をリアルタイムでチェック
    useEffect(() => {
        if (!questionDeadline) return;
        const interval = setInterval(() => {
            setIsQuestionPeriodActive(new Date() < questionDeadline);
        }, 1000);
        return () => clearInterval(interval);
    }, [questionDeadline]);

    // 選択されたクイズの回答詳細を取得
    useEffect(() => {
        if (!token || !selectedQuizForDetail) return;
        
        const fetchAnswerDetails = async () => {
            try {
                const res = await getMyMonouAnswers(token);
                const filtered = res.data.filter((a: MonouUserAnswer) => a.quiz === selectedQuizForDetail);
                setQuizAnswerDetails(filtered);
            } catch (error) {
                console.error('回答詳細の取得に失敗:', error);
            }
        };
        
        fetchAnswerDetails();
    }, [selectedQuizForDetail, token]);

    // スタッフパネルの状態を復元（リロード時）とURLパラメータ処理
    useEffect(() => {
        // 認証状態のローディング中は何もしない
        if (isLoading) {
            return;
        }
        
        // スタッフでない場合は強制的に閉じる
        if (!isStaff) {
            setShowStaffPanel(false);
            localStorage.removeItem('monouStaffPanelOpen');
            return;
        }
        
        // URLパラメータで開く
        const params = new URLSearchParams(location.search);
        const panel = params.get('panel');
        if (panel === 'staff') {
            setShowStaffPanel(true);
            localStorage.setItem('monouStaffPanelOpen', 'true');
            return;
        }
        
        // localStorage から状態を復元
        const savedState = localStorage.getItem('monouStaffPanelOpen');
        if (savedState === 'true') {
            setShowStaffPanel(true);
        }
    }, [isStaff, location.search, isLoading]);

    const fetchData = useCallback(async () => {
        if (!token) return;

        try {
            // 自分の質問を取得
            const questionsRes = await getMyMonouQuestions(token);
            setMyQuestions(questionsRes.data);

            // 公開中のクイズを取得
            const quizzesRes = await getPublishedMonouQuizzes(token);
            setPublishedQuizzes(quizzesRes.data);

            // 自分の回答を取得
            const answersRes = await getMyMonouAnswers(token);
            setMyAnswers(answersRes.data);

            // スタッフの場合は全質問と全クイズを取得
            if (isStaff) {
                const allQuestionsRes = await getAllMonouQuestions(token);
                setAllQuestions(allQuestionsRes.data);

                const allQuizzesRes = await getAllMonouQuizzes(token);
                setAllQuizzes(allQuizzesRes.data);
            }

            // 結果発表状態を取得
            const resultStatusRes = await getMonouResultPublishStatus(token);
            setIsResultPublished(resultStatusRes.data.is_result_published || false);
        } catch (error: any) {
            if (error.response && error.response.status === 401) {
                try {
                    const newToken = await refreshTokenFunction();
                    if (newToken) {
                        fetchData();
                    }
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError);
                }
            } else {
                console.error('データ取得エラー:', error);
            }
        }
    }, [token, isStaff, refreshTokenFunction]);

    // 質問を投稿
    const handlePostQuestion = async () => {
        if (!token || !questionText.trim()) return;

        try {
            await postMonouQuestion(token, questionText);
            setQuestionText('');
            setMessage({ type: 'success', text: '質問を投稿しました' });
            fetchData();
        } catch (error) {
            setMessage({ type: 'error', text: '質問の投稿に失敗しました' });
        }
    };

    // 質問を編集
    const handleUpdateQuestion = async (id: number) => {
        if (!token || !editingQuestionText.trim()) return;

        try {
            await updateMonouQuestion(token, id, editingQuestionText);
            setEditingQuestionId(null);
            setEditingQuestionText('');
            setMessage({ type: 'success', text: '質問を更新しました' });
            fetchData();
        } catch (error) {
            setMessage({ type: 'error', text: '質問の更新に失敗しました' });
        }
    };

    // 質問を削除
    const handleDeleteQuestion = async (id: number) => {
        if (!token || !window.confirm('この質問を削除しますか?')) return;

        try {
            await deleteMonouQuestion(token, id);
            setMessage({ type: 'success', text: '質問を削除しました' });
            fetchData();
        } catch (error) {
            setMessage({ type: 'error', text: '質問の削除に失敗しました' });
        }
    };

    // 回答を送信
    const handleSubmitAnswer = async () => {
        if (!token || selectedChoice === null) return;

        const currentQuiz = publishedQuizzes[currentQuizIndex];
        if (!currentQuiz) return;

        // 既に回答済みかチェック
        const alreadyAnswered = myAnswers.some(answer => answer.quiz === currentQuiz.id);
        if (alreadyAnswered) {
            setMessage({ type: 'error', text: 'この問題には既に回答済みです' });
            return;
        }

        try {
            await submitMonouAnswer(token, currentQuiz.id, selectedChoice);
            setSelectedChoice(null);
            setMessage({ type: 'success', text: '回答を送信しました' });
            fetchData();

            // 次の問題へ
            if (currentQuizIndex < publishedQuizzes.length - 1) {
                setCurrentQuizIndex(currentQuizIndex + 1);
            }
        } catch (error) {
            setMessage({ type: 'error', text: '回答の送信に失敗しました' });
        }
    };

    // ランキングを取得
    const handleShowRanking = async () => {
        if (!token) return;

        try {
            const res = await getMonouRanking(token);
            // すべてのエントリに selected_count が存在することを確認
            const rankingData = res.data.map((entry: RankingEntry) => ({
                ...entry,
                selected_count: entry.selected_count ?? 0,
                correct_count: entry.correct_count ?? 0
            }));
            setRanking(rankingData);
            setShowRanking(true);
        } catch (error) {
            setMessage({ type: 'error', text: 'ランキングの取得に失敗しました' });
        }
    };

    // スタッフ: 質問を選定
    const handleSelectQuestion = async (id: number, isSelected: boolean) => {
        if (!token) return;

        try {
            await selectMonouQuestion(token, id, isSelected);
            setMessage({ type: 'success', text: isSelected ? '質問を選定しました' : '選定を解除しました' });
            fetchData();
        } catch (error) {
            setMessage({ type: 'error', text: '選定の更新に失敗しました' });
        }
    };

    // スタッフ: クイズを公開
    const handlePublishQuiz = async (quizId: number) => {
        if (!token) return;

        try {
            await publishMonouQuiz(token, quizId);
            setMessage({ type: 'success', text: 'クイズを公開しました' });
            fetchData();
        } catch (error) {
            setMessage({ type: 'error', text: 'クイズの公開に失敗しました' });
        }
    };

    // 現在のクイズを取得
    const currentQuiz = publishedQuizzes[currentQuizIndex];
    const hasAnsweredCurrentQuiz = currentQuiz && myAnswers.some(answer => answer.quiz === currentQuiz.id);

    // 認証状態のローディング中はローディング表示
    if (isLoading) {
        return (
            <Box
                sx={{
                    marginTop: `${HEADER_HEIGHT + 16}px`,
                    padding: 2,
                    minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                }}
            >
                <Typography variant="h6">読み込み中...</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                marginTop: `${HEADER_HEIGHT + 16}px`,
                padding: 2,
                minHeight: `calc(100vh - ${HEADER_HEIGHT}px)`,
                boxSizing: 'border-box',
                background: 'linear-gradient(135deg, #fff8e1 0%, #ffe0b2 100%)',
            }}
        >
            {/* タイトル */}
            <Paper
                sx={{
                    padding: 3,
                    maxWidth: 800,
                    margin: '0 auto',
                    marginBottom: 3,
                    background: 'linear-gradient(135deg, #ffcc80 0%, #ffb74d 100%)',
                    color: '#e65100',
                    textAlign: 'center',
                    borderRadius: 4,
                    boxShadow: '0 8px 32px rgba(255, 152, 0, 0.15)',
                    border: '1.5px solid #ffb74d',
                }}
            >
                <Typography
                    variant="h4"
                    gutterBottom
                    sx={{
                        fontWeight: 'bold',
                        mb: 2,
                        color: '#e65100',
                        letterSpacing: 2,
                        textShadow: '0 2px 8px #fff8e1',
                        userSelect: 'none',
                    }}
                >
                    <span role="img" aria-label="crown">👑</span>もん王<span role="img" aria-label="crown">👑</span>
                </Typography>
                <Typography variant="body1" sx={{ color: '#e65100', fontWeight: 500 }}>
                    質問投票箱＆クイズイベント
                </Typography>
            </Paper>

            {message && (
                <Alert
                    severity={message.type}
                    onClose={() => setMessage(null)}
                    sx={{ 
                        maxWidth: 800, 
                        position: 'fixed',
                        bottom: 100,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1300,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        animation: 'slideUp 0.3s ease-in-out',
                        '@keyframes slideUp': {
                            '0%': { transform: 'translateX(-50%) translateY(100px)', opacity: 0 },
                            '100%': { transform: 'translateX(-50%) translateY(0)', opacity: 1 }
                        }
                    }}
                >
                    {message.text}
                </Alert>
            )}

            {/* スタッフパネルトグル */}
            {isStaff && (
                <Box sx={{ maxWidth: 800, margin: '0 auto 16px', textAlign: 'center' }}>
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={() => {
                            const newState = !showStaffPanel;
                            setShowStaffPanel(newState);
                            localStorage.setItem('monouStaffPanelOpen', String(newState));
                        }}
                        sx={{ fontWeight: 700, borderRadius: 3 }}
                    >
                        {showStaffPanel ? 'スタッフパネルを閉じる' : 'スタッフパネルを開く'}
                    </Button>
                </Box>
            )}

            {/* スタッフ管理パネル */}
            {isStaff && showStaffPanel && (
                <Paper
                    sx={{
                        padding: 3,
                        maxWidth: 800,
                        margin: '0 auto 24px',
                        background: '#fff3e0',
                        borderRadius: 3,
                        boxShadow: '0 4px 16px rgba(255, 152, 0, 0.1)',
                    }}
                >
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#e65100' }}>
                        スタッフ管理パネル
                    </Typography>

                    <Box
                        onClick={() => setExpandQuestions(!expandQuestions)}
                        sx={{
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            p: 1.5,
                            background: '#f5f5f5',
                            borderRadius: 1,
                            mb: 2,
                        }}
                    >
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            １　投稿された質問一覧 ({allQuestions.length}件)：問題にしたい質問を選んでね
                        </Typography>
                        <KeyboardArrowDownIcon
                            sx={{
                                transform: expandQuestions ? 'rotate(180deg)' : 'rotate(0deg)',
                                transition: 'transform 0.3s',
                            }}
                        />
                    </Box>
                    <Collapse in={expandQuestions}>
                        <List>
                            {allQuestions.map((q) => (
                                <ListItem
                                    key={q.id}
                                    sx={{
                                        background: q.is_selected ? '#c8e6c9' : '#fff',
                                        borderRadius: 2,
                                        mb: 1,
                                    }}
                                >
                                    <ListItemText
                                        primary={q.text}
                                        secondary={`投稿者: ${q.created_by_name} | 投稿日: ${new Date(q.created_at).toLocaleString()}`}
                                    />
                                    <Button
                                        size="small"
                                        variant={q.is_selected ? 'outlined' : 'contained'}
                                        onClick={() => handleSelectQuestion(q.id, !q.is_selected)}
                                    >
                                        {q.is_selected ? '選定解除' : '選定'}
                                    </Button>
                                </ListItem>
                            ))}
                        </List>
                    </Collapse>

                    <Divider sx={{ my: 2 }} />

                    {/* 新規クイズ作成 */}
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        ２　新規クイズ作成：選定した質問を選んで、回答を入力してね。正解をひとつ選んで作成ボタン押してね
                    </Typography>
                    <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: '1fr', mb: 2 }}>
                        <FormControl fullWidth>
                            <InputLabel id="monou-question-select-label">質問を選択（スタッフ選定済み）</InputLabel>
                            <Select
                                labelId="monou-question-select-label"
                                value={newQuizQuestionId}
                                label="質問を選択（スタッフ選定済み）"
                                onChange={(e) => setNewQuizQuestionId(Number(e.target.value))}
                            >
                                {allQuestions.filter(q => q.is_selected).map(q => (
                                    <MenuItem key={q.id} value={q.id}>{q.text.slice(0, 40)}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        {/* 問題文の入力は不要。選択した質問の本文が問題文になります。 */}
                        <Typography variant="body2" sx={{ mt: 1 }}>選択肢（4つ入力し、正解を1つ選んでください）</Typography>
                        <FormControl component="fieldset">
                            <RadioGroup
                                value={correctIndex ?? ''}
                                onChange={(e) => setCorrectIndex(Number(e.target.value))}
                            >
                                {[0,1,2,3].map((idx) => (
                                    <Box key={idx} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        <FormControlLabel value={idx} control={<Radio />} label={`正解`} />
                                        <TextField
                                            placeholder={`選択肢 ${idx + 1}`}
                                            value={choiceTexts[idx]}
                                            onChange={(e) => {
                                                const next = [...choiceTexts];
                                                next[idx] = e.target.value;
                                                setChoiceTexts(next);
                                            }}
                                            fullWidth
                                        />
                                    </Box>
                                ))}
                            </RadioGroup>
                        </FormControl>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={async () => {
                                if (!token) return;
                                // バリデーション
                                if (!newQuizQuestionId) {
                                    setMessage({ type: 'error', text: '質問を選択してください' });
                                    return;
                                }
                                if (choiceTexts.some(t => !t.trim())) {
                                    setMessage({ type: 'error', text: '選択肢4つすべてに入力してください' });
                                    return;
                                }
                                if (correctIndex === null) {
                                    setMessage({ type: 'error', text: '正解の選択肢を選んでください' });
                                    return;
                                }
                                try {
                                    // 同じ質問でクイズが既に存在しないか確認
                                    const isDuplicate = allQuizzes.some(q => q.question === Number(newQuizQuestionId));
                                    if (isDuplicate) {
                                        setMessage({ type: 'error', text: 'この質問は既にクイズ化されています' });
                                        return;
                                    }

                                    // クイズ作成
                                    const selectedQuestion = allQuestions.find(q => q.id === Number(newQuizQuestionId));
                                    const questionText = selectedQuestion ? selectedQuestion.text : '';
                                    const nextOrder = (allQuizzes.length > 0)
                                        ? Math.max(...allQuizzes.map(q => q.order)) + 1
                                        : 1;
                                    const quizRes = await createMonouQuiz(
                                        token,
                                        Number(newQuizQuestionId),
                                        questionText,
                                        choiceTexts[correctIndex],
                                        nextOrder
                                    );
                                    const quizId = quizRes.data.id;

                                    // 選択肢を作成
                                    const choicePromises = [];
                                    for (let i = 0; i < 4; i++) {
                                        choicePromises.push(
                                            createMonouQuizChoice(
                                                token,
                                                quizId,
                                                choiceTexts[i],
                                                i + 1,
                                                i === correctIndex
                                            )
                                        );
                                    }
                                    // すべての選択肢作成を並行実行して待機
                                    await Promise.all(choicePromises);

                                    setMessage({ type: 'success', text: 'クイズを作成しました' });
                                    // フォーム初期化
                                    setNewQuizQuestionId('');
                                    // 問題文・順序の入力は不要のため初期化も不要
                                    setChoiceTexts(['', '', '', '']);
                                    setCorrectIndex(null);

                                    // 再読込
                                    await fetchData();
                                } catch (error: any) {
                                    console.error('クイズ作成エラー:', error);
                                    setMessage({ 
                                        type: 'error', 
                                        text: error?.response?.data?.detail || 'クイズ作成に失敗しました。お手数ですが画面をリロードしてください。' 
                                    });
                                }
                            }}
                            disabled={allQuestions.filter(q => q.is_selected).length === 0}
                        >
                            クイズを作成
                        </Button>
                    </Box>

                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                        ３　作成済みクイズ：順番を変えたり、削除したり
                    </Typography>
                    <List>
                        {allQuizzes.sort((a, b) => a.order - b.order).map((quiz, index) => {
                            const sortedQuizzes = allQuizzes.sort((a, b) => a.order - b.order);
                            const isFirst = index === 0;
                            const isLast = index === sortedQuizzes.length - 1;

                            return (
                                <ListItem
                                    key={quiz.id}
                                    sx={{
                                        background: quiz.is_published ? '#bbdefb' : '#fff',
                                        borderRadius: 2,
                                        mb: 1,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mr: 1 }}>
                                        <IconButton
                                            size="small"
                                            disabled={isFirst}
                                            onClick={async () => {
                                                if (!token) return;
                                                const prevQuiz = sortedQuizzes[index - 1];
                                                if (!prevQuiz) return;
                                                try {
                                                    await Promise.all([
                                                        updateMonouQuiz(token, quiz.id, { order: prevQuiz.order }),
                                                        updateMonouQuiz(token, prevQuiz.id, { order: quiz.order })
                                                    ]);
                                                    setMessage({ type: 'success', text: '順序を更新しました' });
                                                    await fetchData();
                                                } catch {
                                                    setMessage({ type: 'error', text: '順序の更新に失敗しました' });
                                                }
                                            }}
                                        >
                                            <KeyboardArrowUpIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            disabled={isLast}
                                            onClick={async () => {
                                                if (!token) return;
                                                const nextQuiz = sortedQuizzes[index + 1];
                                                if (!nextQuiz) return;
                                                try {
                                                    await Promise.all([
                                                        updateMonouQuiz(token, quiz.id, { order: nextQuiz.order }),
                                                        updateMonouQuiz(token, nextQuiz.id, { order: quiz.order })
                                                    ]);
                                                    setMessage({ type: 'success', text: '順序を更新しました' });
                                                    await fetchData();
                                                } catch {
                                                    setMessage({ type: 'error', text: '順序の更新に失敗しました' });
                                                }
                                            }}
                                        >
                                            <KeyboardArrowDownIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                    <ListItemText
                                        primary={`問題${quiz.order}: ${quiz.question_text}`}
                                        secondary={`回答数: ${quizAnswerCounts[quiz.id] || 0}人 | ${quiz.is_published ? '公開済み' : '未公開'}`}
                                    />
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        {!quiz.is_published ? (
                                            <Button
                                                size="small"
                                                variant="contained"
                                                color="primary"
                                                onClick={() => handlePublishQuiz(quiz.id)}
                                            >
                                                公開
                                            </Button>
                                        ) : (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                color="warning"
                                                onClick={async () => {
                                                    if (!token) return;
                                                    try {
                                                        await updateMonouQuiz(token, quiz.id, { is_published: false });
                                                        setMessage({ type: 'success', text: 'クイズを非公開にしました' });
                                                        await fetchData();
                                                    } catch {
                                                        setMessage({ type: 'error', text: '非公開に失敗しました' });
                                                    }
                                                }}
                                            >
                                                非公開
                                            </Button>
                                        )}
                                        <IconButton
                                            size="small"
                                            color="error"
                                            onClick={async () => {
                                                if (!token || !window.confirm(`問題${quiz.order}を削除しますか?`)) return;
                                                try {
                                                    await deleteMonouQuiz(token, quiz.id);
                                                    setMessage({ type: 'success', text: 'クイズを削除しました' });
                                                    await fetchData();
                                                } catch {
                                                    setMessage({ type: 'error', text: 'クイズの削除に失敗しました' });
                                                }
                                            }}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                </ListItem>
                            );
                        })}
                    </List>

                    <Divider sx={{ my: 2 }} />

                    {/* 公開状況表示 */}
                    <Box sx={{ mb: 2, p: 2, background: '#fff', borderRadius: 2, border: '2px solid #ff9800' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            現在の状況：結果発表中は、答えとランキングも表示されちゃうよ
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                            当日、ヨーイドンで公開してね
                            <Chip 
                                label={`未公開: ${allQuizzes.filter(q => !q.is_published).length}件`}
                                color="default"
                                sx={{ fontWeight: 600 }}
                            />
                            <Chip 
                                label={`公開中: ${allQuizzes.filter(q => q.is_published).length}件`}
                                color="success"
                                sx={{ fontWeight: 600 }}
                            />
                            全員が回答終わって先に結果発表してから、ライブ後とかに公開してね
                            <Chip 
                                label={isResultPublished ? '結果発表中' : '結果未発表'}
                                color={isResultPublished ? 'error' : 'default'}
                                sx={{ fontWeight: 600 }}
                            />
                        </Box>
                    </Box>

                    {/* 一括操作ボタン */}
                    <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={async () => {
                                if (!token) return;
                                try {
                                    await bulkPublishMonouQuizzes(token, 'unpublish');
                                    setMessage({ type: 'success', text: '全てのクイズを非公開にしました' });
                                    await fetchData();
                                } catch {
                                    setMessage({ type: 'error', text: '一括非公開に失敗しました' });
                                }
                            }}
                        >
                            全て非公開
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            color="success"
                            onClick={async () => {
                                if (!token) return;
                                try {
                                    await bulkPublishMonouQuizzes(token, 'publish');
                                    setMessage({ type: 'success', text: '全てのクイズを公開しました' });
                                    await fetchData();
                                } catch {
                                    setMessage({ type: 'error', text: '一括公開に失敗しました' });
                                }
                            }}
                        >
                            全て公開
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            color={isResultPublished ? 'error' : 'primary'}
                            onClick={async () => {
                                if (!token) return;
                                try {
                                    await toggleMonouResultPublish(token, !isResultPublished);
                                    setMessage({ 
                                        type: 'success', 
                                        text: isResultPublished ? '結果を非公開にしました' : '結果を公開しました' 
                                    });
                                    await fetchData();
                                } catch {
                                    setMessage({ type: 'error', text: '結果発表の切り替えに失敗しました' });
                                }
                            }}
                        >
                            {isResultPublished ? '結果発表を終了' : '全員に結果を表示する'}
                        </Button>
                    </Box>
                </Paper>
            )}

            {/* 質問投稿セクション（一般ユーザー用） */}
            {!isStaff && (
                <Paper
                    sx={{
                        padding: 3,
                        maxWidth: 800,
                        margin: '0 auto 24px',
                        background: '#fff3e0',
                        borderRadius: 3,
                        boxShadow: '0 4px 16px rgba(255, 152, 0, 0.1)',
                    }}
                >
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#e65100' }}>
                        質問を投稿
                    </Typography>

                    {/* 投稿上限と期限の情報表示 */}
                    <Box sx={{ mb: 2, p: 2, background: '#fff8dc', borderRadius: 2, border: '2px solid #ffd700' }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100', mb: 1 }}>
                            📋 投稿可能な質問数：{myQuestions.length} / 3件
                        </Typography>
                        {questionDeadline && (
                            <Typography variant="body2" sx={{ color: '#e65100' }}>
                                ⏰ 投稿期限：{questionDeadline.toLocaleString()}
                                {isQuestionPeriodActive ? (
                                    <Typography variant="caption" sx={{ display: 'block', color: '#4caf50', fontWeight: 600, mt: 0.5 }}>
                                        ✅ 投稿受付中
                                    </Typography>
                                ) : (
                                    <Typography variant="caption" sx={{ display: 'block', color: '#d32f2f', fontWeight: 600, mt: 0.5 }}>
                                        ❌ 投稿期限終了
                                    </Typography>
                                )}
                            </Typography>
                        )}
                    </Box>

                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={questionText}
                        onChange={(e) => setQuestionText(e.target.value)}
                        placeholder="もんちゃんに質問したいことを書いてね！この質問をもとに4択問題を作るので、答えが「はい、いいえ」だけにならないようにしてね！"
                        sx={{ mb: 2, background: '#fff' }}
                    />
                    <Button
                        variant="contained"
                        fullWidth
                        onClick={handlePostQuestion}
                        disabled={!questionText.trim() || !isQuestionPeriodActive || myQuestions.length >= 3}
                        sx={{ fontWeight: 700, borderRadius: 3, background: '#ff9800' }}
                    >
                        <SendIcon sx={{ mr: 1 }} />
                        {!isQuestionPeriodActive ? '投稿期限終了' : myQuestions.length >= 3 ? '投稿上限（3件）' : '投稿'}
                    </Button>

                    <Divider sx={{ my: 2 }} />

                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#e65100' }}>
                        あなたの投稿した質問
                    </Typography>
                    <List>
                        {myQuestions.length === 0 ? (
                            <Typography variant="body2" sx={{ color: '#757575', textAlign: 'center', py: 2 }}>
                                まだ質問を投稿していません
                            </Typography>
                        ) : (
                            myQuestions.map((q) => (
                                <ListItem
                                    key={q.id}
                                    sx={{
                                        background: '#fff',
                                        borderRadius: 2,
                                        mb: 1,
                                        border: q.is_selected ? '2px solid #4caf50' : '1px solid #e0e0e0',
                                    }}
                                >
                                    {editingQuestionId === q.id ? (
                                        <Box sx={{ width: '100%' }}>
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={2}
                                                value={editingQuestionText}
                                                onChange={(e) => setEditingQuestionText(e.target.value)}
                                                sx={{ mb: 1 }}
                                            />
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Button size="small" onClick={() => handleUpdateQuestion(q.id)}>
                                                    保存
                                                </Button>
                                                <Button size="small" onClick={() => setEditingQuestionId(null)}>
                                                    キャンセル
                                                </Button>
                                            </Box>
                                        </Box>
                                    ) : (
                                        <>
                                            <ListItemText
                                                primary={q.text}
                                                secondary={
                                                    <>
                                                        投稿日: {new Date(q.created_at).toLocaleString()}
                                                        {q.is_selected && isResultPublished && (
                                                            <Chip
                                                                label="スタッフ選定済み"
                                                                size="small"
                                                                color="success"
                                                                sx={{ ml: 1 }}
                                                            />
                                                        )}
                                                    </>
                                                }
                                            />
                                            <IconButton
                                                onClick={() => {
                                                    setEditingQuestionId(q.id);
                                                    setEditingQuestionText(q.text);
                                                }}
                                                disabled={!isQuestionPeriodActive}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            <IconButton 
                                                onClick={() => handleDeleteQuestion(q.id)} 
                                                color="error"
                                                disabled={!isQuestionPeriodActive}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </>
                                    )}
                                </ListItem>
                            ))
                        )}
                    </List>

                    <Divider sx={{ my: 2 }} />

                    {/* あなたの回答履歴 */}
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#e65100' }}>
                        あなたの回答履歴
                    </Typography>
                    {myAnswers.length === 0 ? (
                        <Typography variant="body2" sx={{ color: '#757575', textAlign: 'center', py: 2 }}>
                            まだ回答していません
                        </Typography>
                    ) : (
                        <List>
                            {myAnswers.map((answer) => {
                                const quiz = publishedQuizzes.find(q => q.id === answer.quiz);
                                const selectedChoice = quiz?.choices.find(c => c.id === answer.selected_choice);
                                return (
                                    <ListItem
                                        key={answer.id}
                                        sx={{
                                            background: isResultPublished 
                                                ? (answer.is_correct ? '#c8e6c9' : '#ffcdd2')
                                                : '#f5f5f5',
                                            borderRadius: 2,
                                            mb: 1,
                                        }}
                                    >
                                        <ListItemText
                                            primary={quiz ? `問題${quiz.order}: ${quiz.question_text.substring(0, 40)}...` : '問題情報なし'}
                                            secondary={
                                                <>
                                                    回答: {selectedChoice ? `${selectedChoice.choice_order}番 - ${selectedChoice.choice_text}` : '不明'}
                                                    <br />
                                                    回答日: {new Date(answer.answered_at).toLocaleString()}
                                                    {isResultPublished && (
                                                        <Chip
                                                            label={answer.is_correct ? '✅ 正解' : '❌ 不正解'}
                                                            size="small"
                                                            color={answer.is_correct ? 'success' : 'error'}
                                                            sx={{ ml: 1 }}
                                                        />
                                                    )}
                                                </>
                                            }
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    )}
                </Paper>
            )}

            {/* クイズセクション */}
            <Paper
                sx={{
                    padding: 3,
                    maxWidth: 800,
                    margin: '0 auto 24px',
                    background: '#e3f2fd',
                    borderRadius: 3,
                    boxShadow: '0 4px 16px rgba(33, 150, 243, 0.1)',
                }}
            >
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#1565c0' }}>
                    <QuizIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                    もん王クイズ{isStaff && ' (プレビュー)'}
                </Typography>

                {publishedQuizzes.length === 0 ? (
                    <Typography variant="body1" sx={{ color: '#757575', textAlign: 'center', py: 4 }}>
                        まだクイズが公開されていません
                    </Typography>
                ) : currentQuiz ? (
                    <Card sx={{ background: '#fff', borderRadius: 2, p: 2 }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                問題 {currentQuizIndex + 1} / {publishedQuizzes.length}
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3, fontWeight: 500 }}>
                                {currentQuiz.question_text}
                            </Typography>
                            {currentQuiz.question_created_by_name && (
                                <Typography variant="body2" sx={{ mb: 2, color: '#555' }}>
                                    質問者: {currentQuiz.question_created_by_name}
                                </Typography>
                            )}

                            {hasAnsweredCurrentQuiz ? (
                                <Alert severity="info">この問題には既に回答済みです</Alert>
                            ) : (
                                <>
                                    <FormControl component="fieldset" fullWidth>
                                        <RadioGroup
                                            value={selectedChoice || ''}
                                            onChange={(e) => setSelectedChoice(Number(e.target.value))}
                                        >
                                            {currentQuiz.choices
                                                .sort((a, b) => a.choice_order - b.choice_order)
                                                .map((choice) => (
                                                    <FormControlLabel
                                                        key={choice.id}
                                                        value={choice.id}
                                                        control={<Radio />}
                                                        label={
                                                            isResultPublished && choice.is_correct
                                                                ? `✅ ${choice.choice_text} (正解)`
                                                                : choice.choice_text
                                                        }
                                                        sx={{
                                                            background: isResultPublished && choice.is_correct 
                                                                ? '#c8e6c9' 
                                                                : '#f5f5f5',
                                                            borderRadius: 2,
                                                            mb: 1,
                                                            p: 1,
                                                        }}
                                                    />
                                                ))}
                                        </RadioGroup>
                                    </FormControl>

                                    <Button
                                        variant="contained"
                                        fullWidth
                                        onClick={handleSubmitAnswer}
                                        disabled={selectedChoice === null}
                                        sx={{ mt: 2, fontWeight: 700, borderRadius: 3 }}
                                    >
                                        回答を送信
                                    </Button>
                                </>
                            )}

                            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                                <Button
                                    variant="outlined"
                                    disabled={currentQuizIndex === 0}
                                    onClick={() => {
                                        setCurrentQuizIndex(currentQuizIndex - 1);
                                        setSelectedChoice(null);
                                    }}
                                >
                                    前の問題
                                </Button>
                                <Button
                                    variant="outlined"
                                    disabled={currentQuizIndex >= publishedQuizzes.length - 1}
                                    onClick={() => {
                                        setCurrentQuizIndex(currentQuizIndex + 1);
                                        setSelectedChoice(null);
                                    }}
                                >
                                    次の問題
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                ) : (
                    <Typography variant="body1" sx={{ color: '#757575', textAlign: 'center', py: 4 }}>
                        全てのクイズに回答しました
                    </Typography>
                )}
            </Paper>

            {/* ランキング表示ボタン (スタッフまたは結果発表中) */}
            {(isStaff || isResultPublished) && (
                <Box sx={{ maxWidth: 800, margin: '0 auto 24px', textAlign: 'center' }}>
                    <Button
                        variant="contained"
                        color="warning"
                        onClick={handleShowRanking}
                        sx={{ fontWeight: 700, borderRadius: 3 }}
                    >
                        <EmojiEventsIcon sx={{ mr: 1 }} />
                        ランキングを見る
                    </Button>
                </Box>
            )}

            {/* ランキングダイアログ */}
            <Dialog open={showRanking} onClose={() => setShowRanking(false)} maxWidth="md" fullWidth>
                <DialogTitle sx={{ background: '#ffb74d', color: '#e65100', fontWeight: 700 }}>
                    もん王ランキング
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    {ranking.length === 0 ? (
                        <Typography variant="body1" sx={{ textAlign: 'center', py: 3 }}>
                            まだランキングデータがありません
                        </Typography>
                    ) : (
                        <>
                            <List>
                                {ranking.map((entry, index) => (
                                    <ListItem
                                        key={entry.user__id}
                                        sx={{
                                            background:
                                                index === 0
                                                    ? '#ffd700'
                                                    : index === 1
                                                    ? '#c0c0c0'
                                                    : index === 2
                                                    ? '#cd7f32'
                                                    : '#fff',
                                            borderRadius: 2,
                                            mb: 1,
                                        }}
                                    >
                                        <Typography variant="h6" sx={{ mr: 2, fontWeight: 700 }}>
                                            {index + 1}位
                                        </Typography>
                                        <ListItemText
                                            primary={
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <span>{entry.user__name}</span>
                                                    {index === 0 && isResultPublished && (
                                                        <Chip
                                                            label="👑 もん王"
                                                            sx={{
                                                                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                                                color: '#fff',
                                                                fontWeight: 'bold',
                                                                fontSize: '0.85rem'
                                                            }}
                                                        />
                                                    )}
                                                </Box>
                                            }
                                            primaryTypographyProps={{ component: 'div' }}
                                            secondary={`正解数: ${entry.correct_count} | 採用数: ${entry.selected_count}`}
                                        />
                                    </ListItem>
                                ))}
                            </List>

                            {/* 問題ごとの回答詳細 */}
                            {(isStaff ? allQuizzes : publishedQuizzes).length > 0 && (
                                <Box sx={{ mt: 3, p: 2, background: '#fff3e0', borderRadius: 2 }}>
                                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#e65100' }}>
                                        問題ごとの回答詳細
                                    </Typography>
                                    <FormControl fullWidth sx={{ mb: 2 }}>
                                        <InputLabel>問題を選択</InputLabel>
                                        <Select
                                            value={selectedQuizForDetail}
                                            onChange={(e) => setSelectedQuizForDetail(e.target.value as number)}
                                            label="問題を選択"
                                        >
                                            <MenuItem value="">選択してください</MenuItem>
                                            {(isStaff ? allQuizzes : publishedQuizzes).sort((a, b) => a.order - b.order).map((quiz) => (
                                                <MenuItem key={quiz.id} value={quiz.id}>
                                                    問題{quiz.order}: {quiz.question_text.substring(0, 30)}...
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>

                                    {selectedQuizForDetail && (() => {
                                        const quizList = isStaff ? allQuizzes : publishedQuizzes;
                                        const selectedQuiz = quizList.find(q => q.id === selectedQuizForDetail);
                                        const correctChoice = selectedQuiz?.choices.find(c => c.is_correct);
                                        const correctCount = quizAnswerDetails.filter(a => a.is_correct).length;
                                        const totalAnswers = quizAnswerDetails.length;
                                        const correctPercentage = totalAnswers > 0 ? Math.round((correctCount / totalAnswers) * 100) : 0;
                                        return (
                                            <Box>
                                                {correctChoice && (
                                                    <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, color: '#2e7d32', mb: 2 }}>
                                                        正解: {correctChoice.choice_text}
                                                    </Typography>
                                                )}
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 1 }}>
                                                    正解者割合: {correctCount}/{totalAnswers} ({correctPercentage}%)
                                                </Typography>
                                                <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
                                                    回答者一覧:
                                                </Typography>
                                                {quizAnswerDetails.length === 0 ? (
                                                    <Typography variant="body2" sx={{ color: '#757575', py: 2 }}>
                                                        まだ回答がありません
                                                    </Typography>
                                                ) : (
                                                    <List dense>
                                                        {quizAnswerDetails.map((answer) => (
                                                            <ListItem
                                                                key={answer.id}
                                                                sx={{
                                                                    background: answer.is_correct ? '#c8e6c9' : '#ffcdd2',
                                                                    borderRadius: 1,
                                                                    mb: 0.5,
                                                                }}
                                                            >
                                                                <ListItemText
                                                                    primary={answer.user_name}
                                                                    secondary={
                                                                        answer.is_correct ? '✅ 正解' : '❌ 不正解'
                                                                    }
                                                                />
                                                            </ListItem>
                                                        ))}
                                                    </List>
                                                )}
                                            </Box>
                                        );
                                    })()}
                                </Box>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowRanking(false)}>閉じる</Button>
                </DialogActions>
            </Dialog>

            {/* ホームに戻る */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                    variant="outlined"
                    color="secondary"
                    sx={{
                        fontWeight: 700,
                        borderRadius: 3,
                        borderColor: '#e65100',
                        color: '#e65100',
                        px: 3,
                        py: 1,
                        boxShadow: '0 2px 8px #ffb74d',
                        '&:hover': { background: '#fff3e0', borderColor: '#e65100' },
                    }}
                    onClick={() => navigate('/')}
                >
                    ホームに戻る
                </Button>
            </Box>
        </Box>
    );
};

export default MonouRoom;
